import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {  ModalLumScheduleComponent} from 'src/app/components/ui/modal-lum-schedule/modal-lum-schedule.component';
import { ModalService } from 'src/app/services/modal/modal.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { DeviceService } from 'src/app/services/devices/device.service';
import { getRelaySwitchPath, normalizeWeekdayMask, timeToMinutes } from 'src/app/services/LUM/lum.helper';

const DAYS_OF_WEEK = [
  { index: 0, value: 2,   short: 'L', key: 'monday' },
  { index: 1, value: 4,   short: 'M', key: 'tuesday' },
  { index: 2, value: 8,   short: 'X', key: 'wednesday' },
  { index: 3, value: 16,  short: 'J', key: 'thursday' },
  { index: 4, value: 32,  short: 'V', key: 'friday' },
  { index: 5, value: 64,  short: 'S', key: 'saturday' },
  { index: 6, value: 128, short: 'D', key: 'sunday' },
] as const;

const MAX_PROGRAMS = 59;


@Component({
  selector: 'app-lum-scheduler-programs',
  imports: [CommonModule, TranslateModule],
  templateUrl: './lum-scheduler-programs.component.html',
  styleUrl: './lum-scheduler-programs.component.scss',
})
export class LumSchedulerProgramsComponent implements OnChanges {

  @Input() device = '';
  @Input() schedulerData;
  @Input() astcalData: unknown = null;

  @Output() schedulesChanged = new EventEmitter<void>();

  readonly daysOfWeek = DAYS_OF_WEEK;
  schedules: any[] = [];
  loading = false;

  constructor(
    private _modal: ModalService,
    private _utils: UtilsService,
    private _deviceService: DeviceService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schedulerData']) {
      this.syncSchedulesFromInput();
    }
  }

  private syncSchedulesFromInput(): void {
    const raw = this.schedulerData?.schedules ?? [];
    this.schedules = raw.map((schedule) => ({
      ...schedule,
      cfg: {
        ...schedule.cfg,
        enabled: schedule.cfg?.enabled === true
          || schedule.cfg?.enabled === 1
          || schedule.cfg?.enabled === '1',
      },
    }));
  }

  /** Abre el modal vacío (nueva) o con una programación existente (editar). */
  openScheduleModal(schedule: any | null = null): void {
    const modalRef = this._modal.open(ModalLumScheduleComponent, {
      centered: true,
      size: 'lg',
    });

    modalRef.componentInstance.astcalData = this.astcalData;
    modalRef.componentInstance.schedule = schedule;

    modalRef.result.then(
      (result: any) => this.saveSchedule(result),
      () => {},
    );
  }


// Obtiene el icono del tipo de programación
  getIconStart(type: string): string {
    switch (type) {
      case 'ocaso':
        return 'fas fa-moon';
      case 'orto':
        return 'fas fa-sun';
      default:
        return 'fas fa-clock';
    }
  }

// Convierte el tiempo de astcal a un formato legible
  getTimeAstcalData(time: unknown): string {
    const result = this._utils.parseUnixTimeOfDay(time);
    return result?.timeStr ?? '--:--';
  }

// Convierte el tiempo  a un formato legible
  getTimeFixedData(time: number): string {
    return this._utils.getDateFromMinutes(time / 60);
  }

  readonly getRelaySwitchPath = getRelaySwitchPath;

// Verifica si el día de la semana está seleccionado en la programación
  isWeekdaySelected(schedule: any, dayValue: number): boolean {
    const weekdayMask = normalizeWeekdayMask(schedule?.cfg?.time_cfg?.calendar?.weekday);
    return (weekdayMask & dayValue) !== 0;
  }

// Habilita o deshabilita una programación
  toggleScheduleEnabled(schedule: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;
    const previous = schedule.cfg.enabled;

    schedule.cfg.enabled = checked;

    this._deviceService.updateCollection(this.device, 'scheduler', 'schedules', schedule.id, { enabled: checked }).subscribe({
      next: () => {
        this.schedulesChanged.emit();
      },
      error: () => {
        schedule.cfg.enabled = previous;
        input.checked = previous;
      },
    });
  }



  saveSchedule(result: any): void {
    this.loading = true;
    const cfg = this.lumDraftToApiCfg(result);
    const scheduleId = result?.scheduleId ?? result?.id ?? null;
    console.log('cfg', cfg);
    console.log('scheduleId', scheduleId);
    const request = scheduleId != null
      ? this._deviceService.updateCollection(this.device, 'scheduler', 'schedules', scheduleId, cfg)
      : this._deviceService.createCollection(this.device, 'scheduler', 'schedules', { cfg });

    request.subscribe({
      next: () => {
        this.schedulesChanged.emit();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  deleteSchedule(schedule: any): void {
    this.loading = true;
    this._deviceService.deleteCollection(this.device, 'scheduler', 'schedules', schedule.id).subscribe({
      next: () => {
        this.schedulesChanged.emit();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }



  lumDraftToApiCfg(draft: any): any {
    console.log('draft lumDraftToApiCfg', draft);
    const result = {
      name: draft.name,
      mode: draft.mode,
      enabled: draft.enabled,
      time_cfg: {
        start: {
          type: draft.start.type,
          offset_sec: this.getDurationSec(draft.start.timeStr)
        },
        end: {
          type: draft.end.type,
          offset_sec: this.getDurationSec(draft.end.timeStr)
        },
        duration_sec: 0,
        cycle_on_sec: draft.cycle_on_sec,
        cycle_off_sec: draft.cycle_off_sec,
        calendar: {
          weekday: draft.weekdayMask,
          period_ids: Array.isArray(draft.period_ids) ? draft.period_ids : [],
        },
      },
      actions: [
        {
          component: "relay",
          start_action: draft.relayAction === 'on' ? 'on' : 'off',
          src_id: 0
      }
      ],
    };
    return result;

  }

  getDurationSec(time: string): number {
    const minutes = timeToMinutes(time);
    return minutes * 60;
  }

  getMaxPrograms(): number {
    return MAX_PROGRAMS;
  }

  getPeriodIds(periodIds: number[]): string {
    return periodIds.map(id => id + 1).join(', ');
  }

}
